import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'

import { navLinks } from '@/constants/site'

/* Site chrome for the Fathom stage. Deliberately thin — a hairline keyline and
 * mono micro-labels, the same vocabulary the flythrough uses on its card backs,
 * so it reads as part of the section rather than a layer bolted on top.
 * Sits above the wordmark (z-30) and stays clickable while the field captures
 * the wheel gesture underneath it. */
export const Nav: FC = (): ReactNode => {
    const { pathname } = useRouter()

    return (
        <header className='fixed inset-x-0 top-0 z-50'>
            <nav className='flex items-center justify-between gap-6 border-b border-white/10 bg-stage/40 px-5 py-4 backdrop-blur-md sm:px-9 sm:py-5'>
                <Link
                    href='/'
                    className='font-display text-lg uppercase leading-none tracking-[0.04em] text-white/90 transition-colors duration-500 hover:text-white sm:text-xl'
                >
                    Fathom
                </Link>

                <ul className='flex items-center gap-5 sm:gap-8'>
                    {navLinks.map((link, i) => {
                        const active = pathname === link.href

                        return (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(
                                        'group flex items-baseline gap-1.5 font-mono text-[0.68rem] uppercase tracking-[0.14em] transition-colors duration-500 sm:text-[0.72rem]',
                                        active ? 'text-white/90' : 'text-white/50 hover:text-white/80'
                                    )}
                                >
                                    <span className='hidden text-white/30 transition-colors duration-500 group-hover:text-white/50 sm:inline'>
                                        {String(i + 1).padStart(2, '0')}
                                    </span>

                                    {link.label}
                                </Link>
                            </li>
                        )
                    })}
                </ul>
            </nav>
        </header>
    )
}
